import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  message: string;
  severity: ToastSeverity;
  description?: string;
}

interface ToastContextValue {
  toast: (message: string, severity?: ToastSeverity, description?: string) => void;
  success: (message: string, description?: string) => void;
  error: (message: string, description?: string) => void;
  warning: (message: string, description?: string) => void;
  info: (message: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const toastConfig: Record<ToastSeverity, { icon: string; bg: string; border: string; iconColor: string }> = {
  success: {
    icon: 'ri-checkbox-circle-fill',
    bg: 'bg-background-50',
    border: 'border-l-4 border-l-semantic-success border border-background-200',
    iconColor: 'text-semantic-success',
  },
  error: {
    icon: 'ri-error-warning-fill',
    bg: 'bg-background-50',
    border: 'border-l-4 border-l-semantic-error border border-background-200',
    iconColor: 'text-semantic-error',
  },
  warning: {
    icon: 'ri-alert-fill',
    bg: 'bg-background-50',
    border: 'border-l-4 border-l-semantic-warning border border-background-200',
    iconColor: 'text-semantic-warning',
  },
  info: {
    icon: 'ri-information-fill',
    bg: 'bg-background-50',
    border: 'border-l-4 border-l-semantic-info border border-background-200',
    iconColor: 'text-semantic-info',
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, severity: ToastSeverity = 'info', description?: string) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      setToasts((prev) => [...prev.slice(-3), { id, message, severity, description }]);
      setTimeout(() => dismiss(id), 4500);
    },
    [dismiss]
  );

  const success = useCallback((msg: string, desc?: string) => toast(msg, 'success', desc), [toast]);
  const error   = useCallback((msg: string, desc?: string) => toast(msg, 'error', desc),   [toast]);
  const warning = useCallback((msg: string, desc?: string) => toast(msg, 'warning', desc), [toast]);
  const info    = useCallback((msg: string, desc?: string) => toast(msg, 'info', desc),    [toast]);

  return (
    <ToastContext.Provider value={{ toast, success, error, warning, info }}>
      {children}
      {/* Toast container */}
      <div
        aria-live="polite"
        aria-label="Notifications"
        className="fixed bottom-6 right-6 z-[200] flex flex-col gap-3 pointer-events-none"
      >
        {toasts.map((t) => {
          const cfg = toastConfig[t.severity];
          return (
            <div
              key={t.id}
              role="status"
              className={`
                pointer-events-auto flex items-start gap-3 rounded-xl shadow-floating
                px-4 py-3 min-w-[280px] max-w-sm
                ${cfg.bg} ${cfg.border}
                animate-[toastIn_220ms_cubic-bezier(0.4,0,0.2,1)]
              `}
              style={{ animation: 'toastIn 220ms cubic-bezier(0.4,0,0.2,1)' }}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${cfg.icon} text-base ${cfg.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground-800 font-label">{t.message}</p>
                {t.description && (
                  <p className="text-xs text-foreground-500 mt-0.5 font-label">{t.description}</p>
                )}
              </div>
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
                className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer rounded"
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}